import { useMany, useTranslate } from '@refinedev/core';
import { Card, Empty, Flex, Select, Table, Tooltip, Typography } from 'antd';
import type {
  PublisherListOut,
  RoyaltyReportDetailOut,
  RoyaltyStatementOut,
  RoyaltyStatementSnapshotOut,
} from '@/api/generated/schemas';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { ProductFormatTag } from '@/components/user_catalog/publications/ProductFormatTag';
import { productKind } from '@/components/user_catalog/publications/productKind';
import { usePublicationsByProducts } from '@/components/user_catalog/publications/usePublicationsByProducts';
import { useAdminTable } from '@/hooks/useAdminTable';
import { formatDuration } from '@/utils/duration';
import { formatUnits } from '@/utils/format';
import { formatLedger, formatPayable } from '@/utils/money';
import { HintLabel } from '@/components/common/HintLabel';
import { PayableAmount } from '@/components/settlement/PayableAmount';
import { UnitPriceCell } from '@/components/settlement/UnitPriceCell';
import { NUMERIC_STYLE } from '@/components/settlement/numeric';
import { getSortOrder } from '@/utils/sorters';
import { currentStatements, statementHistory } from '../statements';

const T = 'settlement.royalty_reports';
const M = 'settlement.royalties_monthly';

/**
 * The royalties_monthly rows one statement version was computed from, frozen
 * when the version was written — later drift in the live aggregate does not
 * reach them.
 */
const SnapshotsTable = ({ statementId }: { statementId: number }) => {
  const t = useTranslate();

  const { tableProps, sorters } = useAdminTable<RoyaltyStatementSnapshotOut>({
    resource: 'settlement.royalty_statement_snapshots',
    pagination: { pageSize: 20 },
    sorters: { initial: [{ field: 'amount', order: 'desc' }] },
    filters: {
      permanent: [
        { field: 'statement_id', operator: 'eq', value: statementId },
      ],
    },
  });

  const rows = tableProps.dataSource ?? [];
  const publicationByProduct = usePublicationsByProducts([
    ...new Set(rows.map((r) => r.product_id)),
  ]);

  return (
    <Table
      {...tableProps}
      pagination={{ ...tableProps.pagination, simple: true }}
      rowKey="id"
      scroll={{ x: 'max-content' }}
    >
      <Table.Column
        dataIndex="product_id"
        title={t(`${M}.fields.product_id`)}
        fixed="left"
        width={320}
        sorter
        sortOrder={getSortOrder(sorters, 'product_id')}
        render={(value: number, row: RoyaltyStatementSnapshotOut) => {
          const publication = publicationByProduct.get(value);
          const kind = productKind(publication, value);
          return (
            <Flex vertical gap={4} align="flex-start">
              <Flex gap={4} align="center" wrap>
                {publication ? (
                  <PublicationInfoPopover
                    publication={publication}
                    publicationId={publication.id}
                  />
                ) : (
                  value
                )}
                <Typography.Text type="secondary">
                  {`v${row.product_version}`}
                </Typography.Text>
              </Flex>
              {kind && <ProductFormatTag kind={kind} />}
            </Flex>
          );
        }}
      />
      <Table.Column
        dataIndex="rate_value"
        title={t(`${M}.fields.rate_value`)}
        align="right"
        render={(value: string, row: RoyaltyStatementSnapshotOut) => (
          <span style={NUMERIC_STYLE}>{formatLedger(value, row.currency)}</span>
        )}
      />
      {/* Column titles drop the "unit price" words — the group header
          carries them. */}
      <Table.ColumnGroup title={t(`${M}.fields.unit_price`)}>
        <Table.Column
          dataIndex="unit_price"
          title={
            <HintLabel
              label={t(`${M}.columns.ledger`)}
              hint={t('settlement.hints.unit_price')}
            />
          }
          align="right"
          render={(value: string, row: RoyaltyStatementSnapshotOut) => (
            <UnitPriceCell unitPrice={value} currency={row.currency} />
          )}
        />
        <Table.Column
          dataIndex="payable_unit_price"
          title={
            <HintLabel
              label={t(`${M}.columns.invoice`)}
              hint={t('settlement.hints.payable_unit_price')}
            />
          }
          align="right"
          render={(value: string, row: RoyaltyStatementSnapshotOut) => (
            <span style={NUMERIC_STYLE}>
              {formatPayable(value, row.currency)}
            </span>
          )}
        />
      </Table.ColumnGroup>
      <Table.Column
        dataIndex="basis_seconds"
        title={t(`${M}.fields.basis_seconds`)}
        sorter
        sortOrder={getSortOrder(sorters, 'basis_seconds')}
        render={(value: number) => (
          <Tooltip title={`${value}s`}>{formatDuration(value)}</Tooltip>
        )}
      />
      <Table.Column
        dataIndex="charges_count"
        title={t(`${M}.fields.charges_count`)}
        sorter
        sortOrder={getSortOrder(sorters, 'charges_count')}
      />
      <Table.Column
        dataIndex="consumers_count"
        title={t(`${M}.fields.consumers_count`)}
        sorter
        sortOrder={getSortOrder(sorters, 'consumers_count')}
      />
      <Table.Column
        dataIndex="units_count"
        title={t(`${M}.fields.units_count`)}
        align="right"
        sorter
        sortOrder={getSortOrder(sorters, 'units_count')}
        render={(value: string) => (
          <span style={NUMERIC_STYLE}>{formatUnits(value)}</span>
        )}
      />
      {/* Column titles drop the "amount" word — the group header carries
          it. Pinned right as a whole; the children inherit `fixed`. */}
      <Table.ColumnGroup title={t(`${M}.fields.amount`)} fixed="right">
        <Table.Column
          dataIndex="amount"
          title={
            <HintLabel
              label={t(`${M}.columns.ledger`)}
              hint={t('settlement.hints.amount_ledger')}
            />
          }
          align="right"
          sorter
          sortOrder={getSortOrder(sorters, 'amount')}
          render={(value: string, row: RoyaltyStatementSnapshotOut) => (
            <span style={NUMERIC_STYLE}>
              {formatLedger(value, row.currency)}
            </span>
          )}
        />
        {/* Not in the endpoint's sort enum — a sum of rounded lines is not a
            column the API can order by. */}
        <Table.Column
          dataIndex="payable_amount"
          title={
            <HintLabel
              label={t(`${M}.columns.invoice`)}
              hint={t('settlement.hints.payable_amount')}
            />
          }
          align="right"
          render={(value: string, row: RoyaltyStatementSnapshotOut) => (
            <PayableAmount
              amount={value}
              roundingDelta={row.rounding_delta}
              currency={row.currency}
            />
          )}
        />
      </Table.ColumnGroup>
    </Table>
  );
};

/**
 * Breakdown of one statement version. Defaults to the report publisher's own
 * current share; the pick lives in `?statement=`, so a version is linkable.
 */
export const RoyaltyReportBreakdownTab = ({
  record,
  statementId,
  onStatementChange,
}: {
  record: RoyaltyReportDetailOut;
  statementId?: number;
  onStatementChange: (statementId: number) => void;
}) => {
  const t = useTranslate();
  const current = currentStatements(record.statements);

  // Same ids in the same order as the statements card — one cached request.
  const publisherIds = current.map((s) => s.publisher_id);
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherMap = new Map<number, PublisherListOut>(
    (publishersResult?.data ?? []).map((p: PublisherListOut) => [p.id, p])
  );

  const selected =
    record.statements.find((s) => s.id === statementId) ??
    current.find((s) => s.publisher_id === record.publisher_id) ??
    current[0];

  const publisherName = (publisherId: number) =>
    publisherMap.get(publisherId)?.name ?? `#${publisherId}`;

  // The picker lists invoice lines, so it labels them with the invoice amount.
  // The publisher name repeats the group header so the closed select shows it.
  const versionLabel = (s: RoyaltyStatementOut, isCurrent: boolean) =>
    `${publisherName(s.publisher_id)} · v${s.version} · ${formatPayable(
      s.payable_amount,
      s.currency
    )}${isCurrent ? ` (${t(`${T}.statements.current`)})` : ''}`;

  const options = current.map((s) => ({
    label: publisherName(s.publisher_id),
    options: [s, ...statementHistory(record.statements, s)].map((v) => ({
      value: v.id,
      label: versionLabel(v, v.id === s.id),
    })),
  }));

  if (!selected) {
    return (
      <Card>
        <Empty />
      </Card>
    );
  }

  return (
    <Card
      title={t(`${T}.sections.breakdown`)}
      extra={
        <Select
          value={selected.id}
          options={options}
          onChange={onStatementChange}
          style={{ minWidth: 280 }}
          popupMatchSelectWidth={false}
          aria-label={t(`${T}.fields.statement`)}
        />
      }
    >
      <SnapshotsTable key={selected.id} statementId={selected.id} />
    </Card>
  );
};
